/**
 * JWT utility functions for parsing and extracting user information from access tokens
 */

/**
 * Decode JWT payload without verification (client-side only)
 * @param {string} token - JWT token
 * @returns {object|null} Decoded payload or null if invalid
 */
export function decodeJWTPayload(token) {
    if (!token || typeof token !== 'string') {
        return null;
    }

    try {
        // JWT consists of three parts separated by dots: header.payload.signature
        const parts = token.split('.');
        if (parts.length !== 3) {
            console.warn('Invalid JWT format');
            return null;
        }

        // Get the payload (second part)
        let base64Payload = parts[1];
        
        // Decode base64url to base64
        base64Payload = base64Payload.replace(/-/g, '+').replace(/_/g, '/');
        
        // Add padding if needed
        const padding = base64Payload.length % 4;
        if (padding > 0) {
            base64Payload += '='.repeat(4 - padding);
        }
        
        // Decode base64url in browser environment
        const decodedPayload = atob(base64Payload)
        
        // Parse JSON
        return JSON.parse(decodedPayload);
    } catch (error) {
        console.error('Error decoding JWT payload:', error);
        return null;
    }
}

/**
 * Get user ID from JWT token
 * @param {string} token - JWT token
 * @returns {string|null} User ID from 'sub' claim
 */
export function getUserIdFromToken(token) {
    const payload = decodeJWTPayload(token);
    return payload?.sub || null;
}

/**
 * Get user email from JWT token
 * @param {string} token - JWT token
 * @returns {string|null} User email
 */
export function getUserEmailFromToken(token) {
    const payload = decodeJWTPayload(token);
    return payload?.email || null;
}

/**
 * Get user roles from JWT token
 * Auth0 typically stores roles in custom claims or permissions
 * @param {string} token - JWT token
 * @returns {string[]} Array of user roles
 */
export function getUserRolesFromToken(token) {
    const payload = decodeJWTPayload(token);
    if (!payload) return [];

    // Check multiple possible claim names for roles based on actual token structures
    const rolesClaims = [
        'samform/roles',           // Keycloak custom claim
        'realm_access.roles',      // Keycloak realm roles  
        'roles',                   // Direct roles claim
        'user_roles',
        'https://samform.no/roles',
        'https://api.samform.no/roles'
    ];

    for (const claim of rolesClaims) {
        if (claim === 'realm_access.roles' && payload.realm_access?.roles) {
            return payload.realm_access.roles;
        } else if (payload[claim] && Array.isArray(payload[claim])) {
            return payload[claim];
        }
    }

    return [];
}

/**
 * Get user permissions from JWT token
 * @param {string} token - JWT token
 * @returns {string[]} Array of user permissions
 */
export function getUserPermissionsFromToken(token) {
    const payload = decodeJWTPayload(token);
    if (!payload) return [];

    // Check multiple possible claim names for permissions
    const permissionsClaims = [
        'permissions',
        'user_permissions',
        'scope', // OAuth2 scopes
        'https://samform.no/permissions',
        'https://api.samform.no/permissions'
    ];

    for (const claim of permissionsClaims) {
        if (payload[claim]) {
            // Handle both array and space-separated string formats
            if (Array.isArray(payload[claim])) {
                return payload[claim];
            } else if (typeof payload[claim] === 'string') {
                return payload[claim].split(' ').filter(p => p.length > 0);
            }
        }
    }

    return [];
}

/**
 * Check if JWT token is expired
 * @param {string} token - JWT token
 * @returns {boolean} True if token is expired
 */
export function isTokenExpired(token) {
    const payload = decodeJWTPayload(token);
    if (!payload || !payload.exp) {
        return true; // Consider invalid tokens as expired
    }

    // JWT exp is in seconds, Date.now() is in milliseconds
    const now = Math.floor(Date.now() / 1000);
    return payload.exp < now;
}

/**
 * Get token expiration date
 * @param {string} token - JWT token
 * @returns {Date|null} Expiration date or null if invalid
 */
export function getTokenExpiration(token) {
    const payload = decodeJWTPayload(token);
    if (!payload || !payload.exp) {
        return null;
    }

    return new Date(payload.exp * 1000);
}